/**
 * Gated HITL bridge for `remove_small_parts` — removes a structure's
 * disconnected parts smaller than a volume threshold IN PLACE, with the same
 * result as the toolbar's Remove small parts panel (same engine call with
 * the threshold in cm³; the structure's contours are replaced with one undo
 * step; approved structures refused).
 *
 * The threshold is REQUIRED: like the panel's input, it is the clinician's
 * choice. When the request has none, the model is told to ask (default
 * 0.5 cm³, the panel's default) rather than this bridge picking one.
 *
 * Flow: parse → resolve the target (shared resolver guards) → refuse
 * approved structures → compute ONCE → refuse when nothing is below the
 * threshold → a {@link RemoveSmallPartsProposal} the card renders (hover
 * shows the result). Apply writes the ALREADY-computed geometry through the
 * shared in-place gate ({@link gateInPlaceProposal}), which refuses a stale
 * preview. Pure/React-free; the engine is injected.
 */
import { DEFAULT_MIN_PART_VOLUME_CM3 } from '@agent/tool-names';
import {
    asRecord,
    failedPlan,
    gateInPlaceProposal,
    mutatingPreamble,
    notModifiableReason,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type ComputedGeometry,
    type InPlaceEditDeps,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { InPlaceEditOutcome, RemoveSmallPartsInput } from '../../contract';

/** The engine result plus the component counts the card discloses. */
export interface RemoveSmallPartsComputed extends ComputedGeometry {
    /** Disconnected parts found in the structure. */
    readonly partsFound: number;
    /** Parts below the threshold, absent from the result. */
    readonly partsRemoved: number;
}

export interface RemoveSmallPartsDeps extends InPlaceEditDeps, PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    readonly geometry: InPlaceEditDeps['geometry'] & {
        applyRemoveSmallParts(
            roiNumber: number,
            minVolumeCm3: number
        ): Promise<RemoveSmallPartsComputed>;
    };
}

export interface RemoveSmallPartsProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    /** The clinician's threshold (cm³). */
    readonly minVolumeCm3: number;
    readonly partsFound: number;
    readonly partsRemoved: number;
    /** After minus before (cm³). */
    readonly volumeDeltaCm3: number;
    apply(): Promise<InPlaceEditOutcome>;
    reject(): InPlaceEditOutcome;
    /** Show a translucent overlay of the result (card hover). */
    showPreview(): void;
    /** Clear the hover overlay. Never deletes the previewed geometry. */
    hidePreview(): void;
}

export type RemoveSmallPartsPlan = MutatingPlan<RemoveSmallPartsProposal>;

/** Defensively read the model-supplied (untyped) tool input. */
function readInput(input: unknown): Partial<RemoveSmallPartsInput> | undefined {
    const o = asRecord(input);
    if (
        o === undefined ||
        typeof o.roi !== 'string' ||
        (o.minVolumeCm3 !== undefined && typeof o.minVolumeCm3 !== 'number')
    ) {
        return undefined;
    }
    return {
        roi: o.roi,
        ...(o.minVolumeCm3 !== undefined
            ? { minVolumeCm3: o.minVolumeCm3 }
            : {}),
    };
}

export async function planRemoveSmallPartsToolCall(
    call: MutatingToolCall,
    deps: RemoveSmallPartsDeps
): Promise<RemoveSmallPartsPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (parsed?.roi === undefined) {
        return failedPlan(call.toolCallId, 'invalid remove_small_parts input');
    }
    const minVolumeCm3 = parsed.minVolumeCm3;
    if (minVolumeCm3 === undefined) {
        return failedPlan(
            call.toolCallId,
            'ask the clinician for the minimum part volume in cm³ (the ' +
                `toolbar default is ${DEFAULT_MIN_PART_VOLUME_CM3} cm³), ` +
                'then call again with it'
        );
    }
    // Never clamped or "fixed": a non-positive value is refused.
    if (!Number.isFinite(minVolumeCm3) || minVolumeCm3 <= 0) {
        return failedPlan(
            call.toolCallId,
            'the minimum part volume must be a positive number of cm³'
        );
    }

    const resolved = resolveOneStructure(
        parsed.roi,
        deps.structures,
        deps.structures.map(toListed),
        'small-part removal',
        'clean up'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3: volumeBeforeCm3 } = resolved.source;
    if (!deps.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiableReason(name));
    }

    // Captured BEFORE computing, so an edit racing the computation is caught.
    const target = {
        roiNumber,
        name,
        volumeBeforeCm3,
        plannedVersion: deps.editVersion(roiNumber),
        plannedContextId: deps.currentContextId(),
    };

    let computed: RemoveSmallPartsComputed;
    try {
        computed = await deps.geometry.applyRemoveSmallParts(
            roiNumber,
            minVolumeCm3
        );
    } catch (err) {
        return failedPlan(call.toolCallId, reasonOf(err));
    }
    // Nothing below the threshold: applying would change nothing. Surface
    // it instead of showing a no-op card.
    if (computed.partsRemoved === 0) {
        deps.geometry.releaseComputed?.(computed.handle);
        return failedPlan(
            call.toolCallId,
            `no part smaller than ${minVolumeCm3} cm³ was found in ` +
                `'${name}' (${computed.partsFound} part(s) in total); ` +
                'nothing to remove'
        );
    }
    if (!(computed.resultVolumeCm3 > 0)) {
        deps.geometry.releaseComputed?.(computed.handle);
        return failedPlan(
            call.toolCallId,
            'every part is smaller than the threshold; the result would be empty'
        );
    }

    const proposal: RemoveSmallPartsProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        minVolumeCm3,
        partsFound: computed.partsFound,
        partsRemoved: computed.partsRemoved,
        volumeDeltaCm3: computed.resultVolumeCm3 - volumeBeforeCm3,
        ...gateInPlaceProposal(computed, target, deps),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
